import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';

import { Involvement } from '../../../models/involvement';
import { Role } from '../../../models/role';

import { AlertService } from '../../../services/common/alert.service';
import { InvolvementService } from '../../../services/matters/involvement.service';

@Component({
  selector: 'app-involvement-form',
  templateUrl: './involvement-form.component.html',
  styleUrls: ['./involvement-form.component.css'],
})
export class InvolvementFormComponent implements OnInit {
  /**
   * Involvement id from parent component if form is for editing a involvement
   */
  @Input() involvement_id: number;

  /**
   * matter id from parent component, indicates matter for the involvement
   */
  @Input() matter_id: number;

  /**
   * Indicates if form is for a new involvement
   */
  @Input() newInvolvementForm: boolean = false;

  /**
   * Sends form result to parent component
   * @param {EventEmitter<string> = new EventEmitter(} ) formResult varaible name
   */
  @Output() formResult: EventEmitter<string> = new EventEmitter();

  /**
   * Form options for select
   * @type {any}
   */
  form_options: any = {
    role_types: [],
  };

  /**
   * Indicates an error on involvement load for edit
   * @type {boolean}
   */
  loadError: boolean = false;

  /**
   * Selected role to add
   * @type {any}
   */
  selectedRoles: Array<any> = [];

  /**
   * Contact info for selected contact
   * @type {any}
   */
  contact_info: any = {
    addresses: [],
    email_addresses: [],
    phone_numbers: [],
    fax_numbers: [],
  };

  /**
   * Involvement object
   */
  involvement: Involvement = new Involvement();

  /**
   * Search term for relationships search field
   * @type {string}
   */
  relationSearchTerm: string;

  /**
   * Indicates if form is being submitted
   * @type {boolean}
   */
  formSubmitting: boolean = false;

  /**
   * Indicates if form has been submitted
   * @type {boolean}
   */
  formSubmitted: boolean = false;

  /**
   * Constructor
   * @param {AlertService}       private alertService       Service for UI notifications using bootstrap alerts
   * @param {InvolvementService} private involvementService Service to access involvements controller
   */
  constructor(private alertService: AlertService, private involvementService: InvolvementService) {
    this.involvementService.getFormOptions().then((response_json) => {
      for(let role_type of response_json.role_types){
        role_type.label = role_type.name;
        role_type.value = role_type.id;
      }
      this.form_options = response_json;
    });
  }

  // Component lifecycle methods

  /**
   * Called on component initialization
   */
  ngOnInit() {
    if (this.involvement_id) this.involvementEdit(this.involvement_id);
    else if (this.matter_id) this.involvement.matter_id = this.matter_id;
  }

  // Component methods

  /**
   * Add address to involvement
   * @param {any} address Address to add
   */
  addressSelect(address: any) {
    if (address.checked) this.involvement.address_list_ids.push(address.id);
    else this.involvement.address_list_ids.splice(this.involvement.address_list_ids.indexOf(address.id), 1);
  }

  /**
   * Cancel form
   */
  cancelForm() {
    this.formResult.emit('Form Cancelled');
  }

  /**
   * Remove all addresses from the involvement
   */
  clearAddresses() {
    for (let address of this.contact_info.addresses) address.checked = false;
    this.involvement.address_list_ids = [];
  }

  /**
   * Remove all email addresses from the involvement
   */
  clearEmailAddresses() {
    for (let email_address of this.contact_info.email_addresses) email_address.checked = false;
    this.involvement.email_ids = [];
  }

  /**
   * Clear all fax numbers from the involvement
   */
  clearFaxNumbers() {
    for (let fax_number of this.contact_info.fax_numbers) fax_number.checked = false;
    this.involvement.fax_phone_number_id = null;
  }

  /**
   * Cleat all phone numbers from the involvement
   */
  clearPhoneNumbers() {
    for (let phone_number of this.contact_info.phone_numbers) phone_number.checked = false;
    this.involvement.phone_number_id = null;
  }

  /**
   * Add email address to involvement
   * @param {any} email_address Email address to add
   */
  emailAddressSelect(email_address: any) {
    if (email_address.checked) {
      this.involvement.email_ids.push(email_address.id);
      this.involvement.use_email = true;
    } else this.involvement.email_ids.splice(this.involvement.email_ids.indexOf(email_address.id), 1);
  }

  /**
   * Select fax number for involvement
   * @param {any} fax_number Fax number to select
   */
  faxNumberSelect(fax_number: any) {
    fax_number.checked = true;
    this.involvement.fax_phone_number_id = fax_number.id;
  }

  /**
   * Fetch contact with its info for the involvement to use
   * @param {any} contact_id Id of the contact
   */
  fetchContact(contact_id: any) {
    this.involvement.contact_id = contact_id;
    this.involvementService.getContactInfo(contact_id).then((response_json) => {
      this.contact_info = response_json;
      if (this.involvement.id) {
        for (let address of this.contact_info.addresses) if (this.involvement.address_list_ids.indexOf(address.id) > -1) address.checked = true;
        for (let email_address of this.contact_info.email_addresses) if (this.involvement.email_ids.indexOf(email_address.id) > -1) email_address.checked = true;
        for (let phone_number of this.contact_info.phone_numbers) if (this.involvement.phone_number_id == phone_number.id) phone_number.checked = true;
        for (let fax_number of this.contact_info.fax_numbers) if (this.involvement.fax_phone_number_id == fax_number.id) fax_number.checked = true;
      } else {
        if (this.contact_info.addresses && this.contact_info.addresses.length > 0) {
          this.contact_info.addresses[0].checked = true;
          this.addressSelect(this.contact_info.addresses[0]);
        }
        if (this.contact_info.email_addresses && this.contact_info.email_addresses.length > 0) {
          this.contact_info.email_addresses[0].checked = true;
          this.emailAddressSelect(this.contact_info.email_addresses[0]);
        }
        if (this.contact_info.phone_numbers && this.contact_info.phone_numbers.length > 0) {
          this.contact_info.phone_numbers[0].checked = true;
          this.phoneNumberSelect(this.contact_info.phone_numbers[0]);
        }
        if (this.contact_info.fax_numbers && this.contact_info.fax_numbers.length > 0) {
          this.contact_info.fax_numbers[0].checked = true;
          this.faxNumberSelect(this.contact_info.fax_numbers[0]);
        }
      }
    });
  }

  /**
   * Edit an involvement
   * @param {any} involvement_id Id of involvement to edit
   */
  involvementEdit(involvement_id: any) {
    this.involvementService.getInvolvementEdit(involvement_id).then((response_json) => {
      if (response_json) {
        this.involvement = response_json;
        console.log(this.involvement, response_json, 'editing')
        this.loadError = false;
        this.fetchContact(this.involvement.contact_id);
        if (this.form_options.role_types.length > 0) 
          for (let role_type of this.form_options.role_types) 
            for (let role of this.involvement.roles) 
              if (role.role_type_id == role_type.id || role.role_type_id == role_type.value) 
                role_type.checked = true;
        this.setRoles(this.form_options.role_types.filter((role_type: any) => role_type.checked));
      } else this.loadError = true;
    });
  }

  /**
   * Select contact and load involvement for edit if contact is already an involvement
   * @param {any} contact_id Id of contact selected
   */
  onContactSelect(contact_id: any) {
    let involvements = this.involvementService.involvementsComponent.involvements;
    for (let involvement of involvements)
      if (involvement.contact_id == contact_id) {
        this.involvementEdit(involvement.id);
        return;
      }
    this.involvement = new Involvement();
    if (this.matter_id) this.involvement.matter_id = this.matter_id;
    this.fetchContact(contact_id);
  }

  /**
   * Select phone number for involvement
   * @param {any} phoneNumber Phone number to select
   */
  phoneNumberSelect(phoneNumber: any) {
    if (!phoneNumber.checked && this.contact_info && this.contact_info.phone_numbers) for (let phoneNumberItem of this.contact_info.phone_numbers) phoneNumberItem.checked = false;
    phoneNumber.checked = true;
    this.involvement.phone_number_id = phoneNumber.id;
  }

  /**
   * Add relationship to involvement
   * @param {any} involvement_autocomplete_item Relationship to add
   */
  relationOnSelect(involvement_autocomplete_item: any) {
    for (let relationship of this.involvement.relationships) if (relationship.related_id == involvement_autocomplete_item.id) return;
    this.involvement.relationships.push({
      related_id: involvement_autocomplete_item.id,
      label: involvement_autocomplete_item.label,
    });
  }

  /**
   * Remove relationship from involvement
   * @param {any} relationship Relationship to remove
   */
  removeRelationship(relationship: any) {
    this.involvement.relationships.splice(this.involvement.relationships.indexOf(relationship), 1);
  }

  /**
   * Reset involvement form to new involvement
   */
  resetForm() {
    this.involvement = new Involvement();
    this.contact_info = {
      addresses: [],
      email_addresses: [],
      phone_numbers: [],
      fax_numbers: [],
    };
  }

  /**
   * Save involvement
   * @param {boolean} formValid Indicates if form is valid
   */
  saveInvolvement(formValid: boolean) {
    this.formSubmitted = true;
    if (formValid && this.involvement.contact_id) {
      this.formSubmitting = true;
      this.involvementService
        .saveInvolvement(this.involvement)
        .then((response_json) => {
          console.log('saveInvolvement', response_json);
          this.formSubmitting = false;
          if (response_json.success || response_json.text == 'Involvement created' || response_json.text == 'Involvement updated') {
            this.resetForm();
            this.alertService.alert_status_success('Contact saved to matter');
            this.formResult.emit(response_json.text || 'Involvement saved');
          } else this.alertService.alert_status_danger(response_json.text || 'Error saving contact to matter');
        })
        .catch(() => {
          this.formSubmitting = false;
          this.alertService.alert_status_danger('Error saving contact to matter');
        });
    }
  }

  setRoles(roleTypes: Array<any>) {
    console.log(roleTypes);
    this.selectedRoles = roleTypes.sort((roleType1, roleType2) => roleType1.name.localeCompare(roleType2.name));
    let indexesToSplice: Array<number> = [];
    for(let idx in this.involvement.roles){
      let role: Role = this.involvement.roles[idx];
      if(!this.selectedRoles.some(roleType => roleType.id == role.role_type_id)) {
        indexesToSplice.unshift(Number(idx));
      }
    }
    for(let idx of indexesToSplice){
      this.involvement.roles.splice(idx, 1);
    }

    for(let roleType of this.selectedRoles) {
      if(!this.involvement.roles.some(role => role.role_type_id == roleType.id)) {
        this.involvement.roles.push(new Role(roleType));
      }
    } 
    console.log(this.involvement.roles);
    this.sortRoles();
  }

  /**
   * Sort selected roles of the involvement
   */
  sortRoles() {
    this.involvement.roles.sort(function (a, b) {
      return a.role_type_name.localeCompare(b.role_type_name);
    });
  }
}
